package no.vareflow.web

import no.vareflow.app.domain.*

private fun WebApp.visibleSnapshots(user: UserAccount): List<CountSession> =
    if (seesAllCategories(user)) core.sessions
    else core.sessions.filter { snapshot ->
        snapshot.userId == user.id || snapshot.inventorySessionId?.let { id -> core.assignmentsForSession(id).any { user.id in it.assigneeIds } } == true
    }

/** Analyse page with two sub-tabs: Analyse (live, open month) | Historikk (closed months). */
fun WebApp.renderAnalysePage(root: El, user: UserAccount) {
    val history = analyseView == "historikk"
    root.pageHeader(if (history) t("Historikk", "History") else t("Analyse", "Analysis"), if (history) t("Avsluttede måneder", "Closed months") else t("Live fra den åpne måneden", "Live from the open month"))
    root.div("analyse-tabs-row") {
        div("sub-tabs") {
            attr("role", "tablist")
            listOf("analyse" to t("Analyse", "Analysis"), "historikk" to t("Historikk", "History")).forEach { (key, label) ->
                val selected = analyseView == key
                el("button", "sub-tab" + if (selected) " selected" else "", label) {
                    attr("type", "button")
                    attr("role", "tab")
                    attr("aria-selected", selected.toString())
                    onClick { if (!selected) { analyseView = key; requestRender() } }
                }
            }
        }
        // Same box as the tabs, a little apart from them: makes an Excel or PDF report.
        div("sub-tabs report-box") {
            el("button", "sub-tab", t("Lag rapport", "Make report")) {
                attr("type", "button")
                attr("aria-haspopup", "dialog")
                onClick { showReportDialog(user) }
            }
        }
    }
    if (history) renderHistory(root, user) else renderAnalyse(root, user)
}

// ---------------------------------------------------------------- Analyse (live)

private fun WebApp.renderAnalyse(root: El, user: UserAccount) {
    val session = shownOpenSession()
    if (session == null) {
        root.emptyCard(t("Ingen åpen måned. Analysen vises så snart en måned er opprettet og tellingen er i gang.", "No open month. The analysis appears as soon as a month is created and counting has started."))
        return
    }
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val totals = liveTotals(session, user)
    root.div("muted small", "${formatYearMonth(session.yearMonth)} · ${session.locationName} · ${session.status.label}")
    if (canSeePrice) root.div("metric-total") {
        metricCard(t("Total vareverdi", "Total stock value"), "${formatPrice(totals.value)} kr")
    }
    if (totals.counted == 0) {
        root.emptyCard(t("Ingen produkter er telt ennå. Tallene oppdateres mens dere teller.", "No products have been counted yet. The numbers update while you count."))
    }
    val svinn = core.svinnFor(session.yearMonth)
    if (svinn.isNotEmpty()) root.el("button", "card nav-card svinn-card") {
        attr("type", "button")
        attr("data-key", "analyse-svinn")
        div("grow") {
            div("card-title", t("Svinn", "Svinn"))
            div("muted small", (if (svinn.size == 1) t("1 registrering", "1 registration") else t("${svinn.size} registreringer", "${svinn.size} registrations")) +
                " · " + summarizeSvinn(svinn, core.orderedCategories()).byReason.joinToString(" · ") { (r, v) -> r.label + if (canSeePrice) " ${formatPrice(v)} kr" else "" })
        }
        if (canSeePrice) span("strong", "${formatPrice(svinn.sumOf { it.value })} kr")
        span("chevron", "›").attr("aria-hidden", "true")
        onClick { tab = Tab.MORE; moreView = "svinn"; svinnMonth = session.yearMonth; requestRender() }
    }
    val maxValue = totals.categories.maxOfOrNull { it.value } ?: 0.0
    root.div("card") {
        h2(t("Per hovedvaregruppe", "Per main category"), "card-heading")
        el("table", "lines analysis") {
            el("thead") {
                el("tr") {
                    el("th", "", t("Hovedvaregruppe", "Main category")); el("th", "num", t("Telt", "Counted"))
                    if (canSeePrice) el("th", "num", t("Verdi", "Value"))
                }
            }
            el("tbody") {
                totals.categories.forEach { c ->
                    el("tr") {
                        el("td") {
                            div("", c.category)
                            if (canSeePrice) div("value-bar") {
                                div("value-fill").attr("style", "width: ${if (maxValue <= 0.0) 0 else (c.value / maxValue * 100).toInt()}%")
                            } else div("value-bar") {
                                div("value-fill").attr("style", "width: ${if (c.total == 0) 0 else c.counted * 100 / c.total}%")
                            }
                        }
                        el("td", "num muted", "${c.counted} / ${c.total}")
                        if (canSeePrice) el("td", "num strong", "${formatPrice(c.value)} kr")
                    }
                }
            }
            el("tfoot") {
                el("tr") {
                    el("td", "strong", t("Total", "Total"))
                    el("td", "num strong", "${totals.counted} / ${totals.total}")
                    if (canSeePrice) el("td", "num strong", "${formatPrice(totals.value)} kr")
                }
            }
        }
    }
    if (canSeePrice && totals.suppliers.isNotEmpty()) {
        root.div("card") {
            h2(t("Per leverandør", "Per supplier"), "card-heading")
            el("table", "lines") {
                el("thead") { el("tr") { el("th", "", t("Leverandør", "Supplier")); el("th", "num", t("Verdi", "Value")) } }
                el("tbody") {
                    totals.suppliers.forEach { (supplier, value) ->
                        el("tr") {
                            el("td", "", supplier)
                            el("td", "num strong", "${formatPrice(value)} kr")
                        }
                    }
                }
            }
            p(t("Produkter uten registrert leverandør er ikke med her.", "Products without a registered supplier are not included here."), "muted tiny")
        }
    }
}

// ---------------------------------------------------------------- Historikk (closed months)

private fun CountSession.value(): Double = lines.sumOf { it.quantity * it.pricePerUnit }

private fun WebApp.renderHistory(root: El, user: UserAccount) {
    val visible = visibleSnapshots(user).sortedWith(compareByDescending<CountSession> { it.yearMonth }.thenByDescending { it.createdAt })
    if (visible.isEmpty()) {
        root.emptyCard(t("Ingen avsluttede måneder ennå. Når en admin avslutter en måned, havner den her.", "No closed months yet. When an admin closes a month, it ends up here."))
        return
    }
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val categoryOf = core.products.associate { it.id to it.category }
    val order = core.orderedCategories()
    visible.groupBy { it.yearMonth.take(4) }.forEach { (year, snapshots) ->
        root.div("year-head") {
            h2(year, "section-title")
            if (canSeePrice) span("muted small", "${formatPrice(snapshots.sumOf { it.value() })} kr")
        }
        snapshots.forEach { snapshot ->
            // Previous closed month at the same location (the list is newest first).
            val previous = visible.firstOrNull { it.locationName == snapshot.locationName && it.yearMonth < snapshot.yearMonth }
            val value = snapshot.value()
            root.el("details", "card snapshot") {
                el("summary", "snapshot-head") {
                    div("grow") {
                        div("strong", "${formatYearMonth(snapshot.yearMonth)} · ${snapshot.locationName}")
                        div("muted small", t("Avsluttet ${formatDateTime(snapshot.createdAt)} · ${snapshot.userName}", "Closed ${formatDateTime(snapshot.createdAt)} · ${snapshot.userName}"))
                        snapshot.serverAckAt?.let { div("ok-text tiny", t("Bekreftet ${formatTime(it)}", "Confirmed ${formatTime(it)}")) }
                    }
                    div("snapshot-figures") {
                        if (canSeePrice) div("strong", "${formatPrice(value)} kr")
                        val change = changeText(value, previous?.value())
                        if (canSeePrice && change != null) {
                            span("change " + if (value >= (previous?.value() ?: 0.0)) "up" else "down", change)
                                .attr("title", t("Endring fra ${formatYearMonth(previous!!.yearMonth)}", "Change from ${formatYearMonth(previous!!.yearMonth)}"))
                        }
                        val (label, tone) = when (snapshot.syncState) {
                            CloudSaveState.LOCAL_SAVED -> t("LOKALT", "LOCAL") to Tone.AMBER
                            CloudSaveState.WAITING_FOR_NETWORK -> t("VENTER NETT", "WAITING FOR NETWORK") to Tone.AMBER
                            CloudSaveState.UPLOADING -> t("SENDER", "SENDING") to Tone.BLUE
                            CloudSaveState.CLOUD_CONFIRMED -> t("BEKREFTET", "CONFIRMED") to Tone.GREEN
                            CloudSaveState.ERROR -> t("FEIL", "ERROR") to Tone.RED
                        }
                        statusBadge(label, tone)
                    }
                }
                // Hovedvare breakdown, in the usual category order.
                val byCategory = snapshot.lines.groupBy { categoryOf[it.productId] ?: t("Ukjent", "Unknown") }
                    .toList().sortedBy { (c, _) -> order.indexOf(c).let { if (it < 0) Int.MAX_VALUE else it } }
                div("snapshot-body") {
                    val month = snapshot.inventorySessionId?.let { id -> core.inventorySessions.firstOrNull { it.id == id } }
                    if (month != null && month.status == InventoryStatus.CLOSED && user.role == Role.ADMIN) {
                        div("row") {
                            span("grow muted small", t("Trenger måneden å telles videre?", "Does the month need more counting?"))
                            button(t("Åpne igjen", "Reopen"), "btn outline small") {
                                if (core.reopenInventorySession(month.id, user)) toast(t("${formatYearMonth(month.yearMonth)} er åpnet igjen.", "${formatYearMonth(month.yearMonth)} has been reopened."))
                            }
                        }
                    }
                    h2(t("Per hovedvaregruppe", "Per main category"), "card-subheading")
                    el("table", "lines") {
                        el("thead") {
                            el("tr") {
                                el("th", "", t("Hovedvaregruppe", "Main category")); el("th", "num", t("Linjer", "Lines"))
                                if (canSeePrice) el("th", "num", t("Verdi", "Value"))
                            }
                        }
                        el("tbody") {
                            byCategory.forEach { (category, lines) ->
                                el("tr") {
                                    el("td", "", category)
                                    el("td", "num muted", lines.size.toString())
                                    if (canSeePrice) el("td", "num strong", "${formatPrice(lines.sumOf { it.quantity * it.pricePerUnit })} kr")
                                }
                            }
                        }
                    }
                    el("details", "all-lines") {
                        el("summary", "link", t("Vis alle linjer (${snapshot.lines.size})", "Show all lines (${snapshot.lines.size})"))
                        el("table", "lines") {
                            el("thead") {
                                el("tr") {
                                    el("th", "", t("Produkt", "Product")); el("th", "", t("Avdeling", "Department")); el("th", "num", t("Antall", "Quantity"))
                                    if (canSeePrice) el("th", "num", t("Verdi", "Value"))
                                }
                            }
                            el("tbody") {
                                snapshot.lines.forEach { line ->
                                    el("tr") {
                                        el("td") {
                                            div("", line.productName)
                                            if (line.note.isNotBlank()) div("muted tiny", t("Kommentar: ${line.note}", "Comment: ${line.note}"))
                                        }
                                        el("td", "muted", line.departmentName)
                                        el("td", "num strong", "${formatQuantity(line.quantity)} ${line.unit}")
                                        if (canSeePrice) el("td", "num", "${formatPrice(line.quantity * line.pricePerUnit)} kr")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
